import React, { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { Post, SkeletonPost, type IPost } from '../../components/Post';
import { DealsExperimentArticle } from './DealsExperimentArticle';
import { DealsExperiment } from '../../constants/dealsExperiment';
import { findPostRegionName, getDealsBannerProps, getDealsLeadFormProps } from './utils';
import {
  ApplicationContext,
  buildPostUrl,
  canDeleteAllImagesFromPosts,
  isOwner,
  parsePostCardPath,
  sanitizePostContent,
  useGetPost,
  usePreparedContent,
} from '../../utils';
import { selectPost } from '../../selectors/post';
import { selectUser } from '../../selectors/user';
import { selectCurrentRegion } from '../../selectors/geo/currentRegion';
import { selectRegions } from '../../selectors/regions/selectRegions';
import { selectCpdRegions } from '../../selectors/cpdRegions';
import {
  getIsPostCommentsUserSubscribed,
  getPost,
  getPostLikeCounts,
  updateBlogPostStatus,
  updateQuestionPostStatus,
} from '../../actions/post';
import * as Analytics from '../../../app/helpers/analytics_helper';
import { POST_COMMENTS_ID, PostCardPrefixAsPostType } from '../../constants';
import { AUTHORS } from '../../constants/authors';
import { ERequestStatus } from '../../types/requestStatus';
import { TThunkDispatch } from '../../types/redux';
import { TDealsContentConfig } from '../../types/dealsContentConfig';
import { TDealsBannerConfig } from '../../types/dealsBannerConfig';
import { EStatus, EType } from '../../repositories/journal/entities/journal/JournalAttributesSchema';

export const PostContainer = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const { pathname } = useLocation();
  const { id: qsPostId, prefix } = parsePostCardPath(pathname);
  const post = useSelector(selectPost);
  const user = useSelector(selectUser);
  const currentRegion = useSelector(selectCurrentRegion);
  const { items: dictionaryRegions } = useSelector(selectRegions);
  const { items: cpdRegionOptions } = useSelector(selectCpdRegions);
  const postType = prefix && PostCardPrefixAsPostType[prefix];
  const history = useHistory();
  const {
    config,
    custom: { deleteAllImagesFromPostsSettings, isDealsBannerExperimentEnabled },
  } = useContext(ApplicationContext);

  const dealsExperimentGroup = DealsExperiment.use();
  const { dealsBannerContainerId } = usePreparedContent(post, dealsExperimentGroup === 'B');
  useGetPost(
    () => {
      const run = async () => {
        await dispatch(getPost({ id: qsPostId }));
        await dispatch(getIsPostCommentsUserSubscribed({ id: qsPostId }));

        if (postType && (EType.Articles === postType || EType.News === postType)) {
          await dispatch(getPostLikeCounts({ postId: qsPostId, postType }));
        }
      };

      void run();
    },
    { dependencyNames: ['pathname'] },
  );

  /** Отправляет пост блога или вопрос на форму редактирования */
  const handleEditButtonClick = useCallback(() => {
    if (!('id' in post) || ![EType.Blogs, EType.Questions].includes(post.attributes.type)) {
      return;
    }

    const {
      id,
      attributes: { type, slug },
    } = post;

    history.push(`/${type === EType.Blogs ? 'add-blog' : 'add-vopros'}/?slug-id=${slug}-${id}`);
  }, [history, post]);

  /** Отправляет пост блога или вопрос на публикацию */
  const handlePublishButtonClick = useCallback(() => {
    if (!('id' in post) || ![EType.Blogs, EType.Questions].includes(post.attributes.type)) {
      return;
    }

    const {
      id,
      attributes: { type },
    } = post;

    Analytics.addPost('add_post');

    if (type === EType.Blogs) {
      void dispatch(updateBlogPostStatus({ id }));
    } else {
      void dispatch(updateQuestionPostStatus({ id }));
    }
  }, [dispatch, post]);

  if (post.status === ERequestStatus.Loading) {
    return <SkeletonPost />;
  }

  if (!('id' in post)) {
    return null;
  }

  const {
    id,
    attributes: {
      authorId,
      authorObject,
      authorsLink,
      commentsCount,
      datePublish,
      image,
      numViews,
      subtitle,
      title,
      type,
      regions,
      slug,
      publishedUpdatedAt,
      content,
      preparedContent,
      status,
      disableComments,
      blockReason,
    },
  } = post;
  const isUserOwner = isOwner(authorId || undefined, (user.isAuthenticated && user.userId) || undefined);
  const isAuthorArticlesPost = Boolean(post.attributes.rubrics?.includes(32));
  const dealsContentConfig = config.get<TDealsContentConfig>('frontend-content.dealsContentConfig');
  const dealsLeadForm = getDealsLeadFormProps(id, isDealsBannerExperimentEnabled, cpdRegionOptions, dealsContentConfig);
  const dealsBannerConfig = config.get<TDealsBannerConfig>('frontend-content.dealsBannerConfig');
  const dealsBanner = getDealsBannerProps(id, dealsLeadForm, dealsBannerConfig);

  const postProps: IPost = {
    authorPostsCount: undefined,
    authorId: authorId || undefined,
    authorObject: [EType.Blogs, EType.Questions].includes(type) ? authorObject : undefined,
    authorLinks: authorsLink?.length ? authorsLink : undefined,
    authorData: isAuthorArticlesPost ? AUTHORS.find(author => author.name === title) : undefined,
    commentsCount,
    content: sanitizePostContent(preparedContent || content || ''),
    dateModified: publishedUpdatedAt,
    datePublished: datePublish,
    image: canDeleteAllImagesFromPosts(new Date(datePublish), deleteAllImagesFromPostsSettings) ? undefined : image,
    numViews,
    postId: id,
    postUrl: buildPostUrl({ type, slug, id }),
    postCommentsUrl: buildPostUrl({ type, slug, id, anchor: POST_COMMENTS_ID }),
    postRegionName: findPostRegionName({
      globalRegionId: currentRegion.id,
      postRegionIds: regions || [],
      dictionaryRegions,
    }),
    subtitle: [EType.Articles, EType.News].includes(type) ? subtitle : undefined,
    title,
    type,
    blockReason:
      [EType.Blogs, EType.Questions].includes(type) && isUserOwner && status === EStatus.Decline
        ? blockReason
        : undefined,
    canViewComments: !disableComments,
    canPublishPost: [EType.Blogs, EType.Questions].includes(type) && isUserOwner && status === EStatus.Draft,
    canEditPost: [EType.Blogs, EType.Questions].includes(type) && isUserOwner,
    onEditButtonClick: handleEditButtonClick,
    onPublishButtonClick: handlePublishButtonClick,
  };

  if (!dealsLeadForm) {
    return <Post {...postProps} />;
  }

  return (
    <DealsExperimentArticle
      dealsLeadForm={dealsLeadForm}
      dealsBanner={dealsBanner}
      dealsBannerContainerId={dealsBannerContainerId}
      group={dealsExperimentGroup}
      postProps={postProps}
    />
  );
};
